import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography, styled } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { passwordRegex } from 'constants/regex';
import { RoutePaths } from 'constants/RoutePaths';
import { useRegisterInvitedUser } from 'hooks/useRegisterInvitedUser';
import { useGetInvitationStatus } from 'hooks/useGetInvitationStatus';
import { InvitationStatus } from 'api/models/InvitationStatus';
import { IssCheckbox } from 'components/IssCheckBox';
import { StyledErrorDiv } from '@theme/styledComponents';
import { createColorFilteredComponent } from 'helpers/styledHelpers';
import {
  SecondaryTextBox,
  DisplayNameInfoTypography,
  PolicyAgreementStack,
  SubmitButton,
} from 'components/views/registration/FinishRegistrationPage/FinishRegistrationPage.styles';

const isValidPassword = (password: string): boolean => passwordRegex.test(password);

type FinishRegistrationFormValues = {
  displayName: string;
  password: string;
  passwordRepeated: string;
  acceptPrivacyPolicy: boolean;
};

const defaultFormValues: FinishRegistrationFormValues = {
  displayName: '',
  password: '',
  passwordRepeated: '',
  acceptPrivacyPolicy: false,
};

const getInvitationParams = (search: string) => {
  const params = new URLSearchParams(search);

  return {
    token: params.get('token'),
    invitationId: params.get('invitationId'),
    language: params.get('language'),
  };
};

const createFinishRegistrationSchema = (t: (key: string) => string) => z.object({
  displayName: z.string(),
  password: z.string().refine((value) => isValidPassword(value), {
    message: t('finishRegistrationPage.invalidPassword'),
  }),
  passwordRepeated: z.string().refine((value) => isValidPassword(value), {
    message: t('finishRegistrationPage.invalidPassword'),
  }),
  acceptPrivacyPolicy: z.boolean().refine((value) => value, {
    message: t('general.required'),
  }),
}).refine((data) => data.password === data.passwordRepeated, {
  message: t('finishRegistrationPage.passwordsDoNotMatch'),
  path: ['passwordRepeated'],
});

const StyledPrivacyErrorDiv = createColorFilteredComponent<{ $color: string }>('div')(({ $color }) => ({
  color: $color,
  marginLeft: '1.6rem',
}));

const StyledPageBox = styled(Box)(({ theme }) => ({
  minHeight: '100dvh',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflowY: 'auto',
  boxSizing: 'border-box',
  padding: theme.spacing(4),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const FinishRegistrationPageMobile = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeated, setShowPasswordRepeated] = useState(false);

  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { registerInvitedUser, registerInvitedUserPending } = useRegisterInvitedUser();
  const { fetchInvitationStatus } = useGetInvitationStatus();
  const finishRegistrationSchema = useMemo(() => createFinishRegistrationSchema(t), [t]);
  const {
    register,
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<FinishRegistrationFormValues>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(finishRegistrationSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const { token, invitationId, language } = useMemo(() => getInvitationParams(location.search), [location.search]);

  useEffect(() => {
    i18n.changeLanguage(language ?? 'pl');
  }, [language, i18n]);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleClickShowPasswordRepeated = () => setShowPasswordRepeated((prev) => !prev);
  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => event.preventDefault();

  const onSubmit = async ({ displayName, password, acceptPrivacyPolicy }: FinishRegistrationFormValues) => {
    if (!token || !invitationId) {
      return;
    }

    const resp = await registerInvitedUser({
      displayName,
      password,
      token,
      invitationId,
      acceptPrivacyPolicy,
    });

    if (resp.success) {
      navigate(RoutePaths.LOGIN);
    }
  };

  useEffect(() => {
    const checkInvitationStatus = async (invitationId: string) => {
      const resp = await fetchInvitationStatus(invitationId);
      if (resp && resp.data) {
        const status = resp.data as InvitationStatus;
        if (status !== InvitationStatus.Available) {
          navigate(RoutePaths.INVITATION_ERROR);
        }
      }
    };

    if (invitationId) {
      checkInvitationStatus(invitationId);
    }
  }, [invitationId, fetchInvitationStatus, navigate]);

  const privacyPolicyLink = useMemo(() => (
    <PolicyAgreementStack>
      <Typography textAlign="left" width="100%" variant="body2">
        <Link to={RoutePaths.PRIVACY_POLICY}>
          {' ' + t('finishRegistrationPage.policyAgreementPart4')}
        </Link>
      </Typography>
    </PolicyAgreementStack>
  ), [t]);

  return (
    <StyledPageBox>
      <StyledTitle variant="h5">{t('finishRegistrationPage.finishRegistration')}</StyledTitle>
      <Stack spacing={theme.spacing(2)} component="form" onSubmit={handleSubmit(onSubmit)}>
        <FormControl variant="outlined">
          <InputLabel htmlFor="displayName">{t('general.displayName')}</InputLabel>
          <OutlinedInput
            data-testid="finish-registration-fullname-input"
            label={t('general.displayName')}
            inputProps={{ id: 'displayName', 'aria-labelledby': 'displayName-label' }}
            {...register('displayName')}
          />
        </FormControl>
        <SecondaryTextBox>
          <DisplayNameInfoTypography variant="body2" fontWeight="400" textAlign="center">
            {t('finishRegistrationPage.displayNameWillBeVisible')}
          </DisplayNameInfoTypography>
        </SecondaryTextBox>
        <FormControl variant="outlined">
          <InputLabel htmlFor="password">{t('general.password')}</InputLabel>
          <OutlinedInput
            data-testid="finish-registration-password-input"
            id="password"
            label={t('general.password')}
            type={showPassword ? 'text' : 'password'}
            {...register('password')}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {errors.password && <StyledErrorDiv>{errors.password.message}</StyledErrorDiv>}
        </FormControl>
        <FormControl variant="outlined">
          <InputLabel htmlFor="passwordRepeated">{t('finishRegistratioNpage.passwordRepeat')}</InputLabel>
          <OutlinedInput
            data-testid="finish-registration-password-repeated-input"
            id="passwordRepeated"
            label={t('finishRegistratioNpage.passwordRepeat')}
            type={showPasswordRepeated ? 'text' : 'password'}
            {...register('passwordRepeated')}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPasswordRepeated}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPasswordRepeated ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {errors.passwordRepeated && <StyledErrorDiv>{errors.passwordRepeated.message}</StyledErrorDiv>}
        </FormControl>
        <SecondaryTextBox>
          <Typography variant="body2">{t('finishRegistrationPage.passwordRequirements')}</Typography>
        </SecondaryTextBox>
        <div>
          <Controller
            name="acceptPrivacyPolicy"
            control={control}
            render={({ field }) => (
              <IssCheckbox
                onHandleChanged={(checked) => {
                  field.onChange(checked);
                  void trigger('acceptPrivacyPolicy');
                }}
                description={t('finishRegistrationPage.acceptPolicyPrivacy')}
                additionalText={privacyPolicyLink}
              />
            )}
          />
          {errors.acceptPrivacyPolicy && (
            <StyledPrivacyErrorDiv $color={theme.palette.red.main}>
              {errors.acceptPrivacyPolicy.message}
            </StyledPrivacyErrorDiv>
          )}
        </div>
        <Stack width="100%" display="flex" justifyContent="center">
          <SubmitButton
            data-testid="finish-registration-submit-button"
            color="primary"
            type="submit"
            loading={registerInvitedUserPending}
            variant="contained"
          >
            {t('finishRegistrationPage.createAccount')}
          </SubmitButton>
        </Stack>
      </Stack>
    </StyledPageBox>
  );
};
